import { useEffect } from 'react';
import { WealthicaStorage } from '@/providers/wealthica2/utils/storage';
import { logger } from '@/services/LoggingService';
import { ACCOUNTS_TTL_MS, POSITIONS_TTL_MS } from '@/providers/wealthica2/utils/query-config';

/**
 * Guards the initial accounts/positions fetch to prevent update-depth loops and unnecessary requests.
 * Extracted from Wealthica2GoldContainer.
 */
export function useInitialWealthicaFetchGuard(
  isWealthicaConnected: boolean,
  storeConnections: any[],
  multiStore: any,
  fetchAccountsNow: () => void,
  initialFetchDoneRef: React.MutableRefObject<boolean>
) {
  useEffect(() => {
    if (!isWealthicaConnected) return;
    if (initialFetchDoneRef.current) return;

    // Check for any available connection source (DB or store)
    const hasAnyConnections = (storeConnections.length || 0) > 0 
      || (multiStore?.connections?.size || 0) > 0;
    if (!hasAnyConnections) return;

    // Skip when a forced fetch is scheduled post-hydration
    try {
      const forcedId = WealthicaStorage.getItem('WEALTHICA2_FORCE_FETCH_INSTITUTION_ID');
      if (forcedId) {
        logger.info('Wealthica2GoldContainer', '⏭️ Skipping initial batch fetch due to scheduled forced fetch', {
          forcedInstitutionId: forcedId
        });
        initialFetchDoneRef.current = true;
        return;
      }
    } catch { /* noop */ }

    const now = Date.now();
    const hasAnyAccounts = (storeConnections || []).some(c => (c.accounts?.length || 0) > 0);
    const anyStale = (storeConnections || []).some(c => {
      const accCount = (c.accounts?.length || 0);
      const last = c.lastAccountsFetch ? new Date(String(c.lastAccountsFetch)).getTime() : 0;
      if (accCount === 0 || !last) return true;
      return (now - last) >= ACCOUNTS_TTL_MS;
    });

    initialFetchDoneRef.current = true;

    if (hasAnyAccounts && !anyStale) {
      // Accounts are fresh; selectively refresh stale positions
      const stalePosConns = (storeConnections || []).filter(c => {
        const posCount = Array.isArray(c.positions) ? (c.positions?.length || 0) : 0;
        const lastPos = c.lastPositionsFetch ? new Date(String(c.lastPositionsFetch)).getTime() : 0;
        return posCount === 0 || !lastPos || (now - lastPos) >= POSITIONS_TTL_MS;
      });

      if (stalePosConns.length > 0) {
        logger.info('Wealthica2GoldContainer', '⏩ Accounts fresh; selectively refreshing stale positions', {
          connectionsToRefresh: stalePosConns.map(c => c.institutionId),
          ttlMs: POSITIONS_TTL_MS
        });
        stalePosConns.forEach(c => {
          try { (multiStore as any).fetchPositionsForInstitution?.(c.institutionId); } catch { /* noop */ }
        });
      } else {
        logger.info('Wealthica2GoldContainer', '⏭️ Skipping initial batch fetch (all connections fresh within TTL)', {
          dbConnections: storeConnections.length || 0,
          storeConnections: multiStore?.connections?.size || 0,
          ttlMs: ACCOUNTS_TTL_MS
        });
      }
      return;
    }

    logger.info('Wealthica2GoldContainer', '🚀 Initial guarded fetch starting', {
      dbConnections: storeConnections.length || 0,
      storeConnections: multiStore?.connections?.size || 0,
      reason: hasAnyAccounts ? 'stale_or_missing_within_ttl' : 'no_accounts_present'
    });

    const timer = setTimeout(() => {
      try { fetchAccountsNow(); } catch (e) {
        logger.error('Wealthica2GoldContainer', 'Initial guarded fetch failed', { error: e });
      }
    }, 250);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWealthicaConnected, storeConnections.length]);
}
